<script>

import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useClasseStore} from "@/stores/Grille/Classe";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const classeStore = useClasseStore();

        const classe = ref({
            byValue: false,
            byRung: false,
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/classes/${id}`).then((response) => {
                classe.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/classes', classe.value).then((response) => {
                    classeStore.addClasse(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/classes', classe.value).then((response) => {
                    classeStore.updateClasse(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/classes/${classe.value.id}`).then(() => {
                    classeStore.deleteClasse(classe.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            classeStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            classe,
            save,
            update,
            remove,
            getBack,
        };
    }
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="700"
    >
        <v-card flat>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <w-label :label="$t('common.name')" required/>
                                <w-text-field
                                    v-model="classe.name"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="6">
                                <w-label label="Echellon" required/>
                                <w-enum-select
                                    v-model="classe.byRung"
                                    :items="[
                                        {id: false, name: $t('common.without_rung')},
                                        {id: true, name: $t('common.with_rung')}
                                     ]"
                                    :readonly="readonly"
                                    not-clearable
                                />
                            </v-col>
                        </v-row>
                        <v-row dense v-if="classe.byRung">
                            <v-col cols="12" md="4">
                                <w-label label="Pèriodicité" required/>
                                <w-text-field
                                    v-model="classe.periodicity"
                                    type="number"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12" md="4" v-if="classe.byRung">
                                <w-label label="Augmentation" required/>
                                <w-enum-select
                                    v-model="classe.byValue"
                                    :items="[
                                        {id: false, name: $t('common.by_percent')},
                                        {id: true, name: $t('common.by_value')}
                                    ]"
                                    not-clearable
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12" md="4" v-if="classe.byValue">
                                <w-label :label="$t('classe.value')" required/>
                                <w-text-field
                                    v-model="classe.value"
                                    type="number"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12" md="4" v-if="!classe.byValue">
                                <w-label :label="$t('common.percent')" required/>
                                <w-text-field
                                    v-model="classe.percenatge"
                                    type="number"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
